import React, { useMemo } from 'react';
import { KeyValue } from '../../stores';
import { KeyValueEditor } from './KeyValueEditor';

interface PathParamsEditorProps {
  url: string;
  pathParams: KeyValue[];
  onChange: (pathParams: KeyValue[]) => void;
}

// Regex to extract :param patterns from URL
const PATH_PARAM_PATTERN = /:([a-zA-Z_][a-zA-Z0-9_]*)/g;

/**
 * Extracts path parameter names from a URL
 * Supports :param syntax (e.g., /api/users/:id/posts/:postId)
 */
export function extractPathParams(url: string): string[] {
  if (!url) return [];

  const params: string[] = [];
  let match;

  // Reset lastIndex to ensure we start from the beginning
  PATH_PARAM_PATTERN.lastIndex = 0;

  while ((match = PATH_PARAM_PATTERN.exec(url)) !== null) {
    const paramName = match[1];
    if (!params.includes(paramName)) {
      params.push(paramName);
    }
  }

  return params;
}

/**
 * Replaces path parameters in URL with their values
 */
export function replacePathParams(url: string, pathParams: KeyValue[]): string {
  if (!url || !pathParams?.length) return url;

  let result = url;
  for (const param of pathParams) {
    if (param.enabled && param.key && param.value) {
      // Replace :paramName with the value
      result = result.replace(
        new RegExp(`:${param.key}(?=[/\\?#]|$)`, 'g'),
        encodeURIComponent(param.value)
      );
    }
  }
  return result;
}

/**
 * Synchronizes path params with URL
 * Adds new params found in URL, removes params no longer in URL
 */
export function syncPathParamsWithUrl(
  url: string,
  existingParams: KeyValue[]
): KeyValue[] {
  const urlParams = extractPathParams(url);
  const existingMap = new Map(
    existingParams.map((p) => [p.key, p])
  );

  // Create params for each URL param, preserving existing values
  const synced = urlParams.map((paramName) => {
    const existing = existingMap.get(paramName);
    if (existing) {
      return existing;
    }
    return {
      key: paramName,
      value: '',
      enabled: true,
      description: '',
    };
  });

  return synced;
}

export function PathParamsEditor({
  url,
  pathParams,
  onChange,
}: PathParamsEditorProps) {
  // Extract params from URL
  const urlParams = useMemo(() => extractPathParams(url), [url]);

  // Show preview URL with substituted values
  const previewUrl = useMemo(
    () => replacePathParams(url, pathParams),
    [url, pathParams]
  );

  // Check if there are any path params in the URL
  if (urlParams.length === 0) {
    return (
      <div className="text-sm text-muted-foreground">
        <p>No path parameters detected in the URL.</p>
        <p className="mt-1 text-xs">
          Use <code className="px-1 py-0.5 bg-muted rounded">:paramName</code> syntax
          in your URL (e.g., /api/users/:id)
        </p>
      </div>
    );
  }

  // Sync path params with URL params
  const handleParamsChange = (newParams: KeyValue[]) => {
    // Only update values for params that exist in the URL
    const filtered = newParams.filter((p) => urlParams.includes(p.key));
    onChange(filtered);
  };

  // Ensure all URL params have entries
  const syncedParams = syncPathParamsWithUrl(url, pathParams);

  return (
    <div className="space-y-3">
      <KeyValueEditor
        items={syncedParams}
        onChange={handleParamsChange}
        placeholder={{ key: 'Parameter', value: 'Value' }}
        keyReadOnly
        showAddButton={false}
      />

      {syncedParams.some((p) => p.value) && (
        <div className="text-xs">
          <span className="text-muted-foreground">Preview: </span>
          <code className="px-1 py-0.5 bg-muted rounded font-mono text-foreground">
            {previewUrl}
          </code>
        </div>
      )}
    </div>
  );
}

export default PathParamsEditor;
